// ---------------------------------------------------------------------------
// Badge and StatusBadge, ported from the design system (R7)
//
// Badge: a hairline pill for short facts beside a name — a channel, a flag, a
// guardrail, a count. Its tone sits in a dot or an icon; the text stays muted.
// StatusBadge: a dot and a word, the only way to show a campaign's or a lead's
// state. A status the system does not list shows its own words on a grey dot,
// never "Draft".
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';

import { cx, node, sentence } from './helpers.js';
import { type IconName, icon } from './icons.js';

export type Tone = 'neutral' | 'info' | 'positive' | 'caution' | 'critical' | 'agent';

/** The pulse of a running campaign: a ring that grows and fades around a dot. */
export function live(): TemplateResult {
  return html`<span class="sdr-live" aria-hidden="true"><span class="sdr-live__ring"></span><span class="sdr-live__dot"></span></span>`;
}

export interface BadgeProps {
  tone?: Tone;
  /** Agent badges carry the agent icon unless another is given. */
  icon?: IconName;
  dot?: boolean;
  live?: boolean;
  title?: string;
  className?: string;
}

export function badge(p: BadgeProps, content?: unknown): TemplateResult {
  const tone = p.tone || 'neutral';
  const name = p.icon || (tone === 'agent' ? 'agent' : null);
  const mark = p.live
    ? live()
    : name
      ? icon({ name, size: 12 })
      : p.dot
        ? html`<span class="sdr-badge__dot" aria-hidden="true"></span>`
        : null;
  return html`<span class=${cx('sdr-badge', `sdr-badge--${tone}`, p.className)} title=${ifDefined(p.title)}
    >${mark}${node(content)}</span
  >`;
}

interface StatusLook {
  tone: Tone;
  label: string;
  hollow?: boolean;
  live?: boolean;
}

/** One map for the campaign lifecycle, SDR Agent's own campaign states, and lead and connection states. */
export const STATUS: Record<string, StatusLook> = {
  draft: { tone: 'neutral', label: 'Draft' },
  scheduled: { tone: 'info', label: 'Scheduled' },
  running: { tone: 'positive', label: 'Running', live: true },
  paused: { tone: 'caution', label: 'Paused' },
  completed: { tone: 'neutral', label: 'Completed', hollow: true },
  error: { tone: 'critical', label: 'Error' },
  queued: { tone: 'neutral', label: 'Queued', hollow: true },
  contacted: { tone: 'info', label: 'Contacted' },
  replied: { tone: 'positive', label: 'Replied' },
  meeting: { tone: 'positive', label: 'Meeting booked' },
  opted_out: { tone: 'neutral', label: 'Opted out' },
  bounced: { tone: 'critical', label: 'Bounced' },
  skipped: { tone: 'neutral', label: 'Skipped', hollow: true },
  active: { tone: 'positive', label: 'Active', live: true },
  generating: { tone: 'info', label: 'Generating' },
  ready_to_launch: { tone: 'caution', label: 'Ready to launch' },
  preparing_to_launch: { tone: 'caution', label: 'Cool-down' },
  archived: { tone: 'neutral', label: 'Archived', hollow: true },
  pending: { tone: 'neutral', label: 'Pending' },
  done: { tone: 'neutral', label: 'Done', hollow: true },
  invite_sent: { tone: 'info', label: 'Invite sent' },
  invite_pending: { tone: 'caution', label: 'Invite pending' },
  invite_received: { tone: 'positive', label: 'They invited us' },
  accepted: { tone: 'positive', label: 'Accepted' },
  dm_sent: { tone: 'info', label: 'DM sent' },
  already_connected: { tone: 'neutral', label: 'Already connected' },
  skipped_already_connected: { tone: 'caution', label: 'Skipped — already connected' },
  invite_ignored: { tone: 'caution', label: 'Skipped — invite ignored' },
  no_invite: { tone: 'neutral', label: 'No invite' },
  connection_unknown: { tone: 'critical', label: 'State unknown' },
};

export interface StatusBadgeProps {
  status: string;
  /** Only to translate, or to say why (a skipped step's reason). */
  label?: string;
  /** For a status the map does not list. */
  tone?: Tone;
  hollow?: boolean;
  live?: boolean;
  title?: string;
  className?: string;
}

export function statusBadge(p: StatusBadgeProps): TemplateResult {
  const s = STATUS[p.status] || { tone: 'neutral', label: sentence(p.status) };
  const tone = p.tone || s.tone;
  const hollow = p.hollow != null ? p.hollow : s.hollow;
  const isLive = p.live != null ? p.live : s.live;
  return html`<span
    class=${cx('sdr-status', `sdr-status--${tone}`, hollow && 'sdr-status--hollow', p.className)}
    title=${ifDefined(p.title)}
    >${isLive ? live() : html`<span class="sdr-status__dot" aria-hidden="true"></span>`}${p.label || s.label}</span
  >`;
}
